import React, { useMemo } from 'react';
import { IconX } from './Icons';
import { Fragrance, JournalEntry, Ingredient } from '../App';

interface Connection {
  userId: string;
  memberNumber: number;
  displayName: string;
  status: 'accepted' | 'pending_in' | 'pending_out';
  since: string;
}

interface MemberCardProps {
  userId: string;
  authToken: string;
  user: {
    firstName: string;
    lastName: string;
    memberNumber: number;
    createdAt?: string;
    unseededFragsAdded?: number;
    seededContributions?: number;
    firstCircle?: boolean;
    connections?: Connection[];
  };
  journalEntries: JournalEntry[];
  catalog: Fragrance[];
  ingredients: Ingredient[];
  archetypeName?: string;
  archetypeColor?: string;
  onClose: () => void;
  onConnectionsChanged: () => void;
}

const CATEGORY_COLORS: Record<string, string> = {
  'Florals': '#E8609A',
  'Woods': '#8B6F47',
  'Citrus': '#F5A623',
  'Spices': '#E8394D',
  'Resins & Orientals': '#9B6FD6',
  'Musks': '#B0A090',
  'Greens & Aquatics': '#16B87A',
  'Fruits': '#FF6B6B',
  'Tobacco & Gourmands': '#6C63FF',
};

const StarBadge: React.FC<{ accent: string; label: string }> = ({ accent, label }) => (
  <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border" style={{ borderColor: accent + '55', background: accent + '0D' }}>
    <svg width="10" height="10" viewBox="0 0 44 42" fill="none" xmlns="http://www.w3.org/2000/svg">
      <polygon
        points="22,2 27.5,16.5 43,16.5 30.5,25.5 35.5,40 22,31 8.5,40 13.5,25.5 1,16.5 16.5,16.5"
        fill={accent}
        stroke={accent}
        strokeWidth="2"
        strokeLinejoin="round"
      />
    </svg>
    <span className="text-xs tracking-wide" style={{ color: accent }}>{label}</span>
  </div>
);

const MemberCard: React.FC<MemberCardProps> = ({
  user,
  journalEntries,
  catalog,
  ingredients,
  archetypeName,
  archetypeColor,
  onClose,
}) => {
  const color = archetypeColor || '#F0C419';

  const topFamilies = useMemo(() => {
    const likedEntries = journalEntries.filter(e => e.rating === 'like' || e.rating === 'love');
    const counts: Record<string, number> = {};
    for (const entry of likedEntries) {
      const frag = catalog.find(f => f.fragranceId === entry.fragranceId);
      if (!frag) continue;
      const allNotes = [...(frag.topNotes || []), ...(frag.middleNotes || []), ...(frag.baseNotes || []), ...(frag.notes || [])];
      for (const note of allNotes) {
        const ing = ingredients.find(i =>
          i.ingredientId === note.toLowerCase().replace(/\s+/g, '-') ||
          i.name.toLowerCase() === note.toLowerCase()
        );
        if (ing?.category) counts[ing.category] = (counts[ing.category] || 0) + 1;
      }
    }
    const total = Object.values(counts).reduce((s, n) => s + n, 0);
    if (total === 0) return [];
    return Object.entries(counts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([cat, count]) => ({ category: cat, pct: Math.round((count / total) * 100) }));
  }, [journalEntries, catalog, ingredients]);

  const contributions = (user.unseededFragsAdded || 0) + (user.seededContributions || 0);
  const joinYear = user.createdAt ? new Date(user.createdAt).getFullYear() : 2026;
  const yearsAsMember = new Date().getFullYear() - joinYear + 1;

  const badgeGroups = [
    {
      section: 'Membership',
      badges: [
        user.memberNumber <= 2 && { accent: color, label: 'Founder' },
        user.firstCircle && { accent: '#9B6FD6', label: 'First Circle' },
      ].filter(Boolean) as { accent: string; label: string }[],
    },
    {
      section: 'Contributions',
      badges: [
        contributions >= 500 && { accent: '#2C2C2C', label: '500' },
        contributions >= 250 && contributions < 500 && { accent: '#2C2C2C', label: '250' },
        contributions >= 100 && contributions < 250 && { accent: '#2C2C2C', label: '100' },
        contributions >= 50 && contributions < 100 && { accent: '#2C2C2C', label: '50' },
        contributions >= 10 && contributions < 50 && { accent: '#2C2C2C', label: '10' },
      ].filter(Boolean) as { accent: string; label: string }[],
    },
    {
      section: 'Years',
      badges: joinYear === 2026 ? [{ accent: '#B0A090', label: 'Year 1' }] : [],
    },
  ].filter(g => g.badges.length > 0);

  const memberNumStr = String(user.memberNumber).padStart(4, '0');

  return (
    <div className="fixed inset-0 bg-charcoal bg-opacity-60 z-50 flex items-center justify-center px-4" onClick={onClose}>
      <div className="relative w-full max-w-sm bg-cream rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="h-1.5 w-full" style={{ background: `linear-gradient(to right, ${color}88, ${color})` }} />
        <button onClick={onClose} className="absolute top-4 right-4 text-stone-light hover:text-charcoal transition-colors z-10"><IconX size={18} /></button>

        <div className="px-6 pt-6 pb-8">

          {/* Identity */}
          <div className="mb-5">
            <p className="text-xs tracking-widest uppercase text-stone-light mb-1">Member #{memberNumStr}</p>
            <h2 className="font-serif text-3xl text-charcoal">{user.firstName} {user.lastName}</h2>
            {archetypeName && (
              <div className="flex items-center gap-2 mt-1">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: color }} />
                <p className="text-sm text-stone">{archetypeName}</p>
              </div>
            )}
          </div>

          {/* Top families */}
          {topFamilies.length > 0 && (
            <div className="mb-5">
              <p className="text-xs tracking-widest uppercase text-stone-light mb-3">Top scent families</p>
              <div className="space-y-2">
                {topFamilies.map(f => (
                  <div key={f.category}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-charcoal">{f.category}</span>
                      <span className="text-xs text-stone-light">{f.pct}%</span>
                    </div>
                    <div className="h-1 bg-cream-dark rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${f.pct}%`, background: CATEGORY_COLORS[f.category] || color }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Catalog contributions + Journal entries */}
          {(contributions > 0 || journalEntries.length > 0) && (
            <div className="mb-5 flex gap-3">
              {contributions > 0 && (
                <div className="flex-1 bg-white border border-cream-dark rounded-xl px-4 py-3 text-center">
                  <p className="font-serif text-2xl text-charcoal">{contributions}</p>
                  <p className="text-xs tracking-widest uppercase text-stone-light mt-0.5">Catalog Contributions</p>
                </div>
              )}
              {journalEntries.length > 0 && (
                <div className="flex-1 bg-white border border-cream-dark rounded-xl px-4 py-3 text-center">
                  <p className="font-serif text-2xl text-charcoal">{journalEntries.length}</p>
                  <p className="text-xs tracking-widest uppercase text-stone-light mt-0.5">Journal Entries</p>
                </div>
              )}
            </div>
          )}

          {/* Badges */}
          {badgeGroups.length > 0 && (
            <div className="mb-6">
              <p className="text-xs tracking-widest uppercase text-stone-light mb-3">Badges</p>
              <div className="flex flex-wrap gap-2">
                {badgeGroups.flatMap(g => g.badges).map((b, i) => <StarBadge key={i} accent={b.accent} label={b.label} />)}
              </div>
            </div>
          )}

          <p className="text-xs text-stone-light text-center mt-8 tracking-widest">SCENTD</p>
        </div>
      </div>
    </div>
  );
};

export default MemberCard;
